import React from "react";
import { useTranslation } from "react-i18next";
import { resolveMarketBadge } from "../utils/marketSignal";

/**
 * MarketSignalBadge — pure presentational component for DealCard surfaces.
 *
 * Three visual tiers, decided ONCE in `valuationTier()`:
 *   1. tier "deal"     → strict signal, no caveat: solid colored badge
 *                        with the exact percentage.
 *   2. tier "estimate" → soft signal on a tight cohort: TRANSPARENT
 *                        background, subtle colored outline + text, and
 *                        an explicit "Schätzung" tag.
 *   3. tier "neutral"  → caveat / weak cohort / broad basis: neutral
 *                        zinc chip, never a green deal promise.
 *
 * Renders inline by default. Pass `inline={false}` for full-width on the
 * listing detail page.
 */
const _VARIANT_CLASSES = {
  // tier "deal" — solid, confident.
  "green-strong":
    "bg-emerald-500 text-zinc-950 border-emerald-400 font-medium",
  green:
    "bg-emerald-500/15 text-emerald-300 border-emerald-500/40 font-medium",
  "neutral-gray":
    "bg-zinc-700/40 text-zinc-300 border-zinc-600",
  orange:
    "bg-orange-500/20 text-orange-300 border-orange-500/40 font-medium",
  red:
    "bg-rose-500/25 text-rose-200 border-rose-500/50 font-medium",
  // tier "estimate" — transparent fill, colored outline only.
  "green-estimate":
    "bg-transparent text-emerald-300/90 border-emerald-500/45",
  "orange-estimate":
    "bg-transparent text-orange-300/90 border-orange-500/45",
  "neutral-estimate":
    "bg-transparent text-zinc-300 border-zinc-600",
  // tier "neutral" — suppressed signal, informational only.
  "neutral-caution":
    "bg-zinc-800/60 text-zinc-300 border-zinc-600",
};

export default function MarketSignalBadge({
  card,
  inline = true,
  className = "",
  /**
   * `full=true` switches caveat + soft labels to the verbose DetailPage
   * wording. Cards use the default compact mode (max 19 chars + `*`
   * sentinel for caveats with extra info in the tooltip).
   */
  full = false,
}) {
  const { t } = useTranslation("market");
  const badge = resolveMarketBadge(card, { full });
  if (!badge) return null;

  // Defence-in-depth CSS: the pill is `whitespace-nowrap` so labels
  // never wrap. On the Card (`inline=true`) we additionally cap the
  // width and ellipsis-truncate so a future too-long label gracefully
  // degrades to "…" instead of overflowing the card frame. The
  // DetailPage (`inline=false`) gets the full label without a width cap.
  const baseCls =
    "rounded-full border text-xs px-2.5 py-1 inline-flex items-center gap-1 whitespace-nowrap overflow-hidden";
  const widthCls = inline
    ? "max-w-[260px] [text-overflow:ellipsis]"
    : "";
  const variantCls =
    _VARIANT_CLASSES[badge.variant] || _VARIANT_CLASSES["neutral-gray"];

  const prefix =
    badge.kind === "caveat" ? "ⓘ " :
    badge.kind === "soft"   ? "≈ " :
    "";

  // testid encodes kind + signal/caveat slug so e2e/screenshots can target
  // specific cards deterministically without coupling to label text.
  const tag =
    badge.kind === "caveat" ? card.market_signal_caveat :
    badge.kind === "strict" ? card.market_signal :
    card.market_signal_soft;
  const testid = `market-signal-badge-${badge.kind}-${tag}`;

  const positionCls = inline ? "" : "self-start";
  const wrapperCls = [
    baseCls, widthCls, variantCls, positionCls, className,
  ].filter(Boolean).join(" ");

  return (
    <span
      data-testid={testid}
      data-kind={badge.kind}
      data-tier={badge.tier}
      data-variant={badge.variant}
      title={badge.tooltip}
      className={wrapperCls}
    >
      <span className="truncate">
        {prefix}{badge.label}
      </span>
      {badge.estimate && (
        <span
          data-testid="market-signal-estimate-tag"
          className="shrink-0 text-[9px] uppercase tracking-[0.12em] opacity-70 border-l border-current/30 pl-1.5 ml-0.5"
        >
          {t("estimateTag")}
        </span>
      )}
    </span>
  );
}
